import { useState } from 'react'

import { ChangePaymentMethodModal } from '@/components/modal/ChangePaymentMethodModal'
import { Button } from '@/components/ui/button'
import {
  WebPanelItem,
  WebPanelItemAction,
  WebPanelItemContent,
  WebPanelItemLabel,
} from '@/components/web-panel'
import { UserPaymentType } from '@/gql/generated/graphql'
import Link from '@/update-monitor/Link'
import type { Maybe } from '@/utils/common-types'
import { FragmentOf, graphql, readFragment } from 'gql.tada'

export interface PaymentMethodPanelItemProps {
  viewer: Maybe<FragmentOf<typeof PaymentMethodPanelItem_viewer>>
}

export function PaymentMethodPanelItem(props: PaymentMethodPanelItemProps) {
  const viewer = readFragment(PaymentMethodPanelItem_viewer, props.viewer)

  switch (viewer?.paymentType) {
    case UserPaymentType.AppleInApp:
    case UserPaymentType.GoogleInApp:
      return (
        <MobileAppPaymentMethod
          paymentType={viewer.paymentType}
          canSwitchToWebPayment={!!viewer.canSwitchToWebPayment}
        />
      )
    case UserPaymentType.OriginalInWeb:
      return (
        <WebPaymentMethod
          gotPaymentProblem={!!viewer.gotPaymentProblem}
          defaultPaymentMethod={viewer.defaultPaymentMethod}
        />
      )
  }
}

interface MobileAppPaymentMethodProps {
  paymentType: 'APPLE_IN_APP' | 'GOOGLE_IN_APP'
  canSwitchToWebPayment: boolean
}
function MobileAppPaymentMethod(props: MobileAppPaymentMethodProps) {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <WebPanelItem>
      <WebPanelItemLabel>支払い方法</WebPanelItemLabel>

      <WebPanelItemContent className="line-clamp-1">
        <span className="text-xs font-bold text-secondary-text no-underline md:text-sm lg:text-[15px]">
          {props.paymentType == UserPaymentType.AppleInApp && (
            <a
              className="text-primary-100"
              href="https://support.apple.com/ja-jp/HT204088"
              target="_blank"
            >
              Appleのヘルプサイト
            </a>
          )}
          {props.paymentType == UserPaymentType.GoogleInApp && (
            <a
              className="text-primary-100"
              href="https://support.google.com/accounts/answer/7673989?hl=ja&co=GENIE.Platform%3DAndroid"
              target="_blank"
            >
              Googleのヘルプサイト
            </a>
          )}
          より確認方法をご確認ください
        </span>
      </WebPanelItemContent>

      {props.canSwitchToWebPayment && (
        <WebPanelItemAction>
          <Button
            variant="outline"
            size="sm"
            className="ml-2 h-[38px] w-[52px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px] lg:w-[80px]"
            onClick={() => setModalOpen(true)}
          >
            変更
          </Button>

          <ChangePaymentMethodModal
            isOpen={modalOpen}
            onRequestClose={() => setModalOpen(false)}
          />
        </WebPanelItemAction>
      )}
    </WebPanelItem>
  )
}

const WebPaymentMethod_defaultPaymentMethod = graphql(`
  fragment WebPaymentMethod_defaultPaymentMethod on PaymentMethod {
    displayName
    ... on CardPaymentMethod {
      brand
      last4
    }
  }
`)
interface WebPaymentMethodProps {
  gotPaymentProblem: boolean
  defaultPaymentMethod: Maybe<
    FragmentOf<typeof WebPaymentMethod_defaultPaymentMethod>
  >
}

function WebPaymentMethod(props: WebPaymentMethodProps) {
  const paymentMethod = readFragment(
    WebPaymentMethod_defaultPaymentMethod,
    props.defaultPaymentMethod
  )

  return (
    <WebPanelItem>
      <WebPanelItemLabel>支払い方法</WebPanelItemLabel>

      <WebPanelItemContent>
        <span className="text-xs font-bold text-[#919191] no-underline md:text-sm lg:text-[15px]">
          {!paymentMethod ? (
            <span className="text-[#EC5519]">
              有効な支払い方法が設定されていません
            </span>
          ) : props.gotPaymentProblem ? (
            <span className="text-[#EC5519]">
              お支払いが正常に完了しませんでした。再登録をお願いします。
            </span>
          ) : paymentMethod?.__typename === 'CardPaymentMethod' ? (
            <span className="inline-flex items-center gap-2">
              <span className="font-bold text-primary-100">
                {paymentMethod.brand}
              </span>
              <span>**** **** **** {paymentMethod.last4}</span>
            </span>
          ) : (
            paymentMethod?.displayName
          )}
        </span>
      </WebPanelItemContent>

      <WebPanelItemAction>
        <Button
          variant="outline"
          size="sm"
          className="ml-2 h-[38px] w-[52px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px] lg:w-[80px]"
          asChild={true}
        >
          <Link href="/web/change-payment-method">変更</Link>
        </Button>
      </WebPanelItemAction>
    </WebPanelItem>
  )
}

export const PaymentMethodPanelItem_viewer = graphql(
  `
    fragment PaymentMethodPanelItem_viewer on User {
      gotPaymentProblem
      paymentType
      canSwitchToWebPayment
      defaultPaymentMethod {
        ...WebPaymentMethod_defaultPaymentMethod
      }
      hasCurriculumLicense
      hasSubscription
    }
  `,
  [WebPaymentMethod_defaultPaymentMethod]
)
